/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import { Grid } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import {
  Button,
  Collapse,
  Pagination,
  Skeleton,
  Space,
  Spin,
  Tooltip,
  Typography,
} from "antd";
import GenericTableContainer from "~/components/GenericTableContainer";

// Material Dashboard 2 React components
import MDBox from "~/components/MDBox";
import DetailsCard from "~/examples/Cards/DetailsCard";

// Billing page components
import { EditNoteOutlined } from "@mui/icons-material";
import moment from "moment";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import EmptyResult from "~/components/Extras/Empty";
import { dateTimeFormat } from "~/config";
import usePagination from "~/hooks/usePagination";
import { getPlantDetails } from "~/services/plant.service";
import { getImageUrl } from "~/utils/utils";
import {
  fieldsQualitative,
  fieldsQuantitative,
  filterFields,
  getMonitoringData,
} from "./helper";

const { Text, Title } = Typography;

function PlantSurveyList() {
  const params = useParams();
  const [items, setItems] = useState([]);
  const navigate = useNavigate();

  const { data, isLoading, isSuccess, mutate, reset } = useMutation({
    queryKey: [params.id],
    mutationFn: (payloads) => getPlantDetails(payloads),
    onSuccess: (data) => {
      console.log("ON SUCCESS 🆘", data);
      setItems(
        data.data?.plant_monitoring_dtls?.map((item) => ({
          key: item["plantationdtl.BarCode_Number"],
          label: item["BarCode_Serial_No"],
          ...item,
        }))
      );
    },
  });
  const { currentPage, handlePageChange, pageSize } = usePagination(
    mutate,
    params.id
  );

  useEffect(() => {
    mutate({ id: params.id, currentPage, pageSize });

    return () => {
      reset();
    };
  }, []);

  const genExtra = (item) => (
    <Space.Compact block>
      <Tooltip title="Update">
        <Button
          onClick={(event) => {
            // If you don't want click extra trigger collapse, you can prevent this:
            event.stopPropagation();
            navigate(`/plant-monitoring/${params.id}`, {
              state: item,
            });
            console.log("second icon");
          }}
          icon={<EditNoteOutlined />}
        >
          Update
        </Button>
      </Tooltip>
    </Space.Compact>
  );
  
  return (
    <GenericTableContainer
      navigateTo={-1}
      cardHeaderTitle="Plant Survey List"
      cardButtonText="Back"
      startIcon={"arrow_back"}
    >
      <MDBox pt={1} pb={2} px={2}>
        {isLoading ? <Skeleton avatar paragraph={{ rows: 4 }} /> : null}
        <Spin tip="Loading..." spinning={isLoading}>
          <Grid container spacing={3}>
            {items?.map((item) => {
              const quantitative = filterFields(item, fieldsQuantitative);
              const qualitative = filterFields(item, fieldsQualitative);
              return (
                <Grid item lg={6} key={item.key}>
                  <Collapse
                    // defaultActiveKey={['1']}
                    accordion={true}
                    size="small"
                    // onChange={(key) => onChange(key, item)}
                    expandIconPosition={"start"}
                    items={[
                      {
                        key: `${data?.data?.barcodedtl?.plant_mstr?.Plant_Scientific_Name}(${data?.data?.barcodedtl?.plant_mstr?.Plant_Eng_Name})`,
                        label: (
                          <Space direction="vertical">
                            <Title level={5}>
                              {`${
                                data?.data?.barcodedtl?.plant_mstr
                                  ?.Plant_Local_Name
                              } - ${params.id ?? ""}`}
                            </Title>
                            <Text strong>
                              Monitoring Type :{" "}
                              {item?.Plantation_Monitoring_Type}
                            </Text>
                            <Text type="secondary">
                              Monitoring Date :{" "}
                              {moment(item?.Created_Dt).format(dateTimeFormat)}
                            </Text>
                          </Space>
                        ),
                        extra: genExtra(item),
                        children: (
                          <MDBox>
                            <DetailsCard
                              plantEnglishName={
                                data?.data?.barcodedtl?.plant_mstr
                                  ?.Plant_Eng_Name
                              }
                              plantScientificName={
                                data?.data?.barcodedtl?.plant_mstr
                                  ?.Plant_Scientific_Name
                              }
                              plantName={
                                data?.data?.barcodedtl?.plant_mstr
                                  ?.Plant_Local_Name
                              }
                              plotName={
                                data?.data?.barcodedtl?.plot_mstr?.Plot_Name
                              }
                              locationName={
                                data?.data?.barcodedtl?.plot_mstr?.loc_mstr
                                  ?.Loc_Name
                              }
                              plantCode={data?.data?.BarCode_Serial_No}
                              monitoringType={item?.Plantation_Monitoring_Type}
                              monitoringRemark={item.Monitoring_Remark}
                              monitoringData={getMonitoringData(
                                item,
                                quantitative,
                                qualitative
                              )}
                              imgSrc={getImageUrl(item?.Plant_Img)}
                            />
                          </MDBox>
                        ),
                      },
                    ]}
                  />
                </Grid>
              );
            })}
            {items?.length === 0 ? (
              <Grid item xs={12} md={12} xl={12} lg={12}>
                <EmptyResult
                  description=" No data available. Click below to create new Monitoring"
                  navigateTo={`/plant-monitoring/${params.id}`}
                  state={{ name: params.id }}
                />
              </Grid>
            ) : null}
            <Grid item lg={12} justifyContent="center">
              <Pagination
                showSizeChanger
                defaultCurrent={currentPage}
                total={data?.count ?? 10}
                onChange={handlePageChange}
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`
                }
              />
            </Grid>
          </Grid>
        </Spin>
      </MDBox>
    </GenericTableContainer>
  );
}

export default PlantSurveyList;
